import { Paper, Typography, Box, Button, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { formatNumber, shortenAddress } from '../../../shared/utils/Formatter';
import { stakerIndexStyle } from '../index.styles';
import {
  getStakeSummary,
  getStakeValidators,
  getStakeVirtuals,
} from '../../../api/stake.api';
import { usePrivy } from '@privy-io/react-auth';
import { useQuery } from '@tanstack/react-query';
import { ethers } from 'ethers';
import { IconCaretDownFilled } from '@tabler/icons-react';
import StakeList from './StakeList';
import { Link } from 'react-router-dom';
import ConvertVirtualDrawer from './ConvertVirtualDrawer';
import UnstakeXVirtualDrawer from './UnstakeXVirtualDrawer';
import { checkNetwork } from '../../../shared/utils/CheckNetwork';
import { useVirtualBalanceContext } from '../../../context/VirtualBalanceContext';
import textDefaultImage from '../../../assets/images/text-default.png';
import voiceDefaultImage from '../../../assets/images/voice-default.png';

const StakeSummary = () => {
  const { user } = usePrivy();
  const [viewBreakdown, setViewBreakdown] = useState<boolean>(false);
  const [openConvertDrawer, setOpenConvertDrawer] = useState<boolean>(false);
  const [openUnstakeDrawer, setOpenUnstakeDrawer] = useState<boolean>(false);
  const { virtualBalance, xVirtualBalance } = useVirtualBalanceContext();

  const { data: totalStakedData } = useQuery({
    queryKey: ['getStakeSummary', user?.wallet?.address],
    queryFn: () => getStakeSummary(user?.wallet?.address || ''),
  });

  const { data: stakeValidators } = useQuery({
    queryKey: ['getStakeValidators', user?.wallet?.address],
    queryFn: () => getStakeValidators(user?.wallet?.address || ''),
  });

  const { data: stakeVirtuals } = useQuery({
    queryKey: ['getStakeVirtuals', user?.wallet?.address],
    queryFn: () => getStakeVirtuals(user?.wallet?.address || ''),
  });

  const formatWeiToEther = (value: string) => {
    return Number(ethers.formatEther(value.toString()));
  };

  const calculatePercentage = (amount: number, totalAmount: number) => {
    return ((amount / totalAmount) * 100).toFixed(2);
  };

  const handleOpenDrawer = async (type: string) => {
    await checkNetwork();

    if (type === 'convert') return setOpenConvertDrawer(true);
    if (type === 'withdraw') return setOpenUnstakeDrawer(true);
  };

  return (
    <>
      {user && totalStakedData && (
        <>
          {!Number(totalStakedData.data.totalStaked) ? (
            <Paper style={{ padding: '1em' }}>
              <Typography>You have not staked any VIRTUAL yet!</Typography>
            </Paper>
          ) : (
            <Paper style={{ padding: '1em' }}>
              <Typography sx={stakerIndexStyle.titleStyle}>
                Staking Stats
              </Typography>

              <Box
                display="flex"
                gap={2}
                flexDirection={['column', 'column', 'row', 'row']}
              >
                <Paper sx={stakerIndexStyle.infoWrapperStyle}>
                  <Box>
                    <Typography sx={stakerIndexStyle.infoBoxHeaderStyle}>
                      Available $xVIRTUAL
                    </Typography>
                    <Typography sx={stakerIndexStyle.contentStyle}>
                      {formatNumber(
                        Number(ethers.formatEther(BigInt(xVirtualBalance)))
                      )}{' '}
                      $xVIRTUAL
                    </Typography>
                    <Typography fontSize={14}>
                      You have{' '}
                      <span
                        style={{
                          fontWeight: '700',
                        }}
                      >
                        {formatNumber(
                          Number(ethers.formatEther(BigInt(virtualBalance)))
                        )}{' '}
                        $VIRTUAL.
                      </span>{' '}
                      Convert them in order to stake in VIRTUAL <br /> and earn{' '}
                      <a href={`/`} style={{ color: '#43BDCE' }}>
                        Convert Now
                      </a>
                    </Typography>
                  </Box>
                </Paper>
                <Paper sx={stakerIndexStyle.infoWrapperStyle}>
                  <Box>
                    <Typography sx={stakerIndexStyle.infoBoxHeaderStyle}>
                      Staked $xVIRTUAL Distribution
                    </Typography>
                    <Typography sx={stakerIndexStyle.contentStyle}>
                      {formatNumber(
                        Number(
                          ethers.formatEther(
                            BigInt(totalStakedData.data.totalStaked)
                          )
                        )
                      )}{' '}
                      $xVIRTUAL
                    </Typography>
                    <Typography fontSize={14}>~ ${formatNumber(0)}</Typography>
                  </Box>
                  <Link to="/stake/withdraw">
                    <Button variant="contained" color="lightPrimary">
                      Withdraw
                    </Button>
                  </Link>
                </Paper>
                {/* <Paper sx={stakerIndexStyle.infoWrapperStyle}>
                  <Box>
                    <Typography sx={stakerIndexStyle.infoBoxHeaderStyle}>
                      Claimable $xVIRTUAL Rewards
                    </Typography>
                    <Typography sx={stakerIndexStyle.contentStyle}>
                      {formatNumber(0)} $xVIRTUAL
                    </Typography>
                    <Typography fontSize={14}>~ ${formatNumber(0)}</Typography>
                  </Box>
                  <Button variant="contained" color="primary">
                    Claim
                  </Button>
                </Paper> */}
              </Box>

              {viewBreakdown && (
                <Box
                  display="flex"
                  gap={2}
                  flexDirection={['column', 'column', 'row', 'row']}
                >
                  {stakeValidators && (
                    <StakeList
                      title="By Validator"
                      data={stakeValidators.data.map((data) => {
                        return {
                          name: shortenAddress(data.walletAddress),
                          imageUrl: `https://effigy.im/a/${data.walletAddress}.svg`,
                          percentage: Number(
                            calculatePercentage(
                              formatWeiToEther(data.totalStaked),
                              formatWeiToEther(totalStakedData.data.totalStaked)
                            )
                          ),
                        };
                      })}
                    />
                  )}

                  {stakeVirtuals && (
                    <StakeList
                      title="By VIRTUALs"
                      data={stakeVirtuals.data.map((data) => {
                        return {
                          name: data.name,
                          imageUrl: data.image
                            ? data.image.url
                            : data.cores[0].name.includes('Voice')
                            ? voiceDefaultImage
                            : textDefaultImage,
                          percentage: Number(
                            calculatePercentage(
                              formatWeiToEther(data.totalStaked),
                              formatWeiToEther(totalStakedData.data.totalStaked)
                            )
                          ),
                        };
                      })}
                    />
                  )}
                </Box>
              )}

              <Typography
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap={1}
                marginTop={1}
                textAlign="center"
                onClick={() => setViewBreakdown(!viewBreakdown)}
              >
                View Staking Breakdown
                <IconCaretDownFilled
                  size={15}
                  style={{
                    transition: '.2s all ease-out',
                    transform: viewBreakdown ? 'rotate(180deg)' : 'rotate(0)',
                  }}
                />
              </Typography>
            </Paper>
          )}
        </>
      )}
      <ConvertVirtualDrawer
        isOpen={openConvertDrawer}
        onClose={() => setOpenConvertDrawer(false)}
      />
      <UnstakeXVirtualDrawer
        isOpen={openUnstakeDrawer}
        onClose={() => setOpenUnstakeDrawer(false)}
      />
    </>
  );
};

export default StakeSummary;
